import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../store';
import { Avatar } from './Avatar';
import { CloseIcon, TrashIcon } from './icons';

/** Lado do quadrado final, em pixels. Todo avatar sai com o mesmo tamanho. */
const LADO = 256;

/**
 * Recorta a imagem num quadrado central e reduz para LADO x LADO, devolvendo
 * WebP. Fazer isso no navegador resolve tres coisas de uma vez: todo avatar
 * tem o mesmo tamanho, a foto que sobe e pequena (o upload nao demora nem
 * estoura o limite), e o servidor nao precisa de biblioteca de imagem.
 */
async function prepararFoto(arquivo: File, zoom: number, deslocX: number, deslocY: number) {
  const bitmap = await createImageBitmap(arquivo);
  const canvas = document.createElement('canvas');
  canvas.width = LADO;
  canvas.height = LADO;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Nao consegui preparar a imagem.');

  // Cobrir o quadrado: a menor dimensao e a que define a escala base.
  const escalaBase = LADO / Math.min(bitmap.width, bitmap.height);
  const escala = escalaBase * zoom;
  const largura = bitmap.width * escala;
  const altura = bitmap.height * escala;

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    bitmap,
    (LADO - largura) / 2 + deslocX,
    (LADO - altura) / 2 + deslocY,
    largura,
    altura,
  );
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', 0.9),
  );
  if (!blob) throw new Error('Nao consegui converter a imagem.');
  return blob;
}

export function PerfilModal({ onClose }: { onClose: () => void }) {
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const toast = useStore((s) => s.toast);

  const [apelido, setApelido] = useState(me?.nickname ?? '');
  const [nome, setNome] = useState(me?.fullName ?? '');
  const [cor, setCor] = useState(me?.accentColor ?? '#FF7A18');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [deslocX, setDeslocX] = useState(0);
  const [deslocY, setDeslocY] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!me) return null;

  function escolher(f: File | null) {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast('Escolha um arquivo de imagem.', 'error');
      return;
    }
    setArquivo(f);
    setZoom(1);
    setDeslocX(0);
    setDeslocY(0);
    setPrevia(URL.createObjectURL(f));
  }

  async function salvar() {
    setSalvando(true);
    try {
      const mudouApelido = apelido.trim() !== me!.nickname;
      const mudouNome = (nome.trim() || null) !== (me!.fullName ?? null);
      const mudouCor = cor !== me!.accentColor;

      if (mudouApelido || mudouNome || mudouCor) {
        const { user } = await api.atualizarPerfil({
          ...(mudouApelido ? { nickname: apelido.trim() } : {}),
          ...(mudouNome ? { fullName: nome.trim() } : {}),
          ...(mudouCor ? { accentColor: cor } : {}),
        });
        setMe(user);
      }

      if (arquivo) {
        const blob = await prepararFoto(arquivo, zoom, deslocX, deslocY);
        await api.enviarFoto(blob);
        // O `me` do estado precisa da versao nova, senao a propria pessoa
        // continuaria vendo a foto antiga ate recarregar a pagina.
        const { user } = await api.me();
        setMe(user);
      }

      toast('Perfil atualizado.');
      onClose();
    } catch (erro) {
      toast(erro instanceof Error ? erro.message : 'Nao consegui salvar.', 'error');
    } finally {
      setSalvando(false);
    }
  }

  async function removerFoto() {
    if (!window.confirm('Remover sua foto de perfil?')) return;
    try {
      await api.removerFoto();
      const { user } = await api.me();
      setMe(user);
      setArquivo(null);
      setPrevia(null);
      toast('Foto removida.');
    } catch (erro) {
      toast(erro instanceof Error ? erro.message : 'Nao consegui remover.', 'error');
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2>Seu perfil</h2>
          <button className="icon-btn" onClick={onClose} title="Fechar">
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          <div className="perfil-foto">
            <div className="perfil-recorte">
              {previa ? (
                <img
                  src={previa}
                  alt=""
                  style={{
                    transform: `translate(${deslocX}px, ${deslocY}px) scale(${zoom})`,
                  }}
                />
              ) : (
                <Avatar
                  name={me.nickname}
                  color={cor}
                  size={128}
                  userId={me.id}
                  avatarVersion={me.avatarVersion}
                />
              )}
            </div>

            <div className="perfil-foto-acoes">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => escolher(e.target.files?.[0] ?? null)}
              />
              <button className="btn ghost small" onClick={() => inputRef.current?.click()}>
                {previa ? 'Trocar imagem' : 'Escolher foto'}
              </button>
              {me.avatarVersion > 0 && !previa && (
                <button className="btn ghost small" onClick={() => void removerFoto()}>
                  <TrashIcon style={{ width: 14, height: 14 }} />
                  Remover
                </button>
              )}
              {previa && (
                <button
                  className="btn ghost small"
                  onClick={() => {
                    setArquivo(null);
                    setPrevia(null);
                  }}
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Os controles de enquadramento so fazem sentido com uma imagem nova. */}
          {previa && (
            <div className="field">
              <label>Enquadramento</label>
              <div className="volume-row">
                <span style={{ fontSize: 11 }}>Zoom</span>
                <input
                  type="range"
                  min={100}
                  max={300}
                  value={Math.round(zoom * 100)}
                  onChange={(e) => setZoom(Number(e.target.value) / 100)}
                />
              </div>
              <div className="volume-row">
                <span style={{ fontSize: 11 }}>↔</span>
                <input
                  type="range"
                  min={-80}
                  max={80}
                  value={deslocX}
                  onChange={(e) => setDeslocX(Number(e.target.value))}
                />
              </div>
              <div className="volume-row">
                <span style={{ fontSize: 11 }}>↕</span>
                <input
                  type="range"
                  min={-80}
                  max={80}
                  value={deslocY}
                  onChange={(e) => setDeslocY(Number(e.target.value))}
                />
              </div>
              <div className="hint">
                A foto sai sempre quadrada, com {LADO}×{LADO}, para todo avatar ficar do mesmo
                tamanho.
              </div>
            </div>
          )}

          <div className="field">
            <label>Nome de usuário</label>
            <input value={apelido} onChange={(e) => setApelido(e.target.value)} maxLength={32} />
            <div className="hint">É com ele que você entra. Não pode repetir com outra pessoa.</div>
          </div>

          <div className="field">
            <label>Nome</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={64}
              placeholder="Seu nome completo"
            />
            <div className="hint">Aparece no seu perfil. Pode ficar em branco.</div>
          </div>

          <div className="field">
            <label>Cor do avatar</label>
            <div className="perfil-cores">
              {['#FF7A18', '#F2560A', '#E0409A', '#8B5CF6', '#2DA6F2', '#30C561', '#F5B544'].map(
                (c) => (
                  <button
                    key={c}
                    className={`perfil-cor ${cor === c ? 'ativa' : ''}`}
                    style={{ background: c }}
                    title={c}
                    onClick={() => setCor(c)}
                  />
                ),
              )}
            </div>
            <div className="hint">Usada quando você não tem foto.</div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn"
            disabled={salvando || apelido.trim().length < 2}
            onClick={() => void salvar()}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
